import React from 'react'

import { DateTime } from 'luxon'

import { Campaign } from '@/core/api/entities/campaign/campaign'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RRuleService } from '@/core/utils/rruleService'
import { AccountBadge } from '@/shared/components/atoms/AccountBadge/AccountBadge'
import Icon from '@/shared/components/atoms/Icon'
import ActionDropdownMenu, {
	ActionMenuItem,
} from '@/shared/components/molecules/ActionDropdownMenu/ActionDropdownMenu'
import { Card } from '@/shared/components/molecules/Card/Card'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

interface CampaignCardProps {
	campaign: Campaign
	menuItems?: ActionMenuItem[]
	onClick?: () => void
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
	campaign,
	menuItems = [],
	onClick,
}) => {
	const { t } = useTranslation('campaigns')
	const currentAccount = useCurrentAccount()
	const showAccountBadge =
		!!campaign.account && campaign.account.id !== currentAccount?.id

	const formatDate = (dateTime: DateTime | null | undefined): string => {
		if (!dateTime) return t('notSet')
		return dateTime.toFormat('EEE d, MMM yyyy')
	}

	const formatRelativeTime = (dateTime: DateTime): string => {
		const now = DateTime.now()
		const diff = now.diff(dateTime, ['months', 'days', 'hours']).toObject()
		if (diff.months && diff.months >= 1)
			return t('relativeTime.months', { count: Math.floor(diff.months) })
		if (diff.days && diff.days >= 1)
			return t('relativeTime.days', { count: Math.floor(diff.days) })
		if (diff.hours && diff.hours >= 1)
			return t('relativeTime.hours', { count: Math.floor(diff.hours) })
		return t('relativeTime.justNow')
	}

	const getDateRange = () => {
		if (!campaign?.rrule) return { start: null, end: null }
		const { dtstart, until } = RRuleService.getRecurrenceDates(campaign.rrule)
		return { start: dtstart, end: until }
	}

	const dateRange = getDateRange()

	return (
		<Card
			className="overflow-hidden cursor-pointer transition-all group bg-card-dark"
			onClick={onClick}
		>
			<div className="px-6 py-5 md:px-6 md:py-4">
				{/* Mobile layout */}
				<div className="flex flex-col gap-4 md:hidden">
					<div className="flex items-start justify-between">
						<span className="bg-outline border px-2 py-1 rounded-md text-sm text-muted-foreground">
							{t('list.id')}: {campaign.id}
						</span>
						{menuItems.length > 0 && (
							<span onClick={(e) => e.stopPropagation()}>
								<ActionDropdownMenu items={menuItems} />
							</span>
						)}
					</div>
					<div className="space-y-2">
						<div className="text-xs text-muted-foreground">
							{campaign.groups?.map((g) => g.name).join(', ') ||
								t('list.noGroups')}
						</div>
						<h3 className="text-lg font-semibold leading-tight">
							{campaign.name}
						</h3>
						{showAccountBadge && (
							<AccountBadge accountName={campaign.account!.name} />
						)}
					</div>
				</div>

				{/* Desktop layout */}
				<div className="hidden md:flex items-start justify-between">
					<div className="flex-1 flex-col lg:flex-row justify-between min-w-0">
						<div>
							<div className="w-full flex flex-row justify-between gap-2">
								<div className="flex items-center gap-3 mb-2">
									<span className="inline-flex items-center rounded-md border border-border bg-shade text-muted-foreground text-sm font-medium px-2 py-1 h-6">
										{t('list.id')}: {campaign.id}
									</span>
								</div>
								<div className="flex flex-row items-start justify-end gap-2">
									<span className="hidden lg:flex lg:flex-row justify-center text-sm text-muted-foreground items-center gap-2 mr-4">
										<Icon name="clock-counter-clockwise" className="w-2 h-2" />
										{t('list.created')} {formatRelativeTime(campaign.createdAt)}
									</span>
									{menuItems.length > 0 && (
										<span onClick={(e) => e.stopPropagation()}>
											<ActionDropdownMenu items={menuItems} />
										</span>
									)}
								</div>
							</div>

							<div className="flex items-center gap-2 mb-1">
								<span className="text-xs text-muted-foreground">
									{campaign.groups?.map((g) => g.name).join(' | ') ||
										t('list.noGroups')}
								</span>
							</div>

							<div className="w-full flex flex-row justify-between gap-2">
								<div className="flex flex-col gap-1.5">
									<h3
										className="text-xl font-semibold cursor-pointer hover:underline"
										onClick={(e) => {
											e.stopPropagation()
											onClick?.()
										}}
									>
										{campaign.name}
									</h3>
									{showAccountBadge && (
										<AccountBadge accountName={campaign.account!.name} />
									)}
								</div>

								<div className="flex w-[60%] flex-col items-end lg:gap-8">
									<div className="flex md:items-end gap-4 lg:gap-10 lg:text-sm text-xs scale-85">
										<div>
											<span className="text-muted-foreground block mb-1">
												{t('list.adsIncluded')}
											</span>
											<span className="font-medium">
												{campaign.adverts?.length || 0}
											</span>
										</div>
										<div>
											<span className="text-muted-foreground block mb-1">
												{t('list.startDate')}
											</span>
											<span className="lg:font-medium text-xs">
												{formatDate(dateRange.start)}
											</span>
										</div>
										<div>
											<span className="text-muted-foreground block mb-1">
												{t('list.endDate')}
											</span>
											<span className="font-medium">
												{formatDate(dateRange.end)}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Card>
	)
}

export default CampaignCard
